import { FieldValues } from 'react-hook-form';

type AnyObject = Record<string, unknown>;

export type FieldDirtyState<T> =
  | boolean
  | (T extends Array<infer U>
      ? FieldDirtyState<U>[]
      : T extends object
        ? { [K in keyof T]?: FieldDirtyState<T[K]> }
        : never);

export function extractDirtyValues<
  T extends FieldValues,
  I extends readonly string[] | Record<string, unknown> | undefined = undefined,
>(dirty: FieldDirtyState<T>, values: T, important?: I): Partial<T> | undefined {
  if (dirty === true) {
    return values;
  }

  //  If we are on an array node.
  if (Array.isArray(dirty)) {
    const result = dirty
      .map((childDirty, idx) => extractDirtyValues(childDirty as FieldDirtyState<unknown>, values[idx], important as I))
      .filter((v) => v !== undefined);

    return result.length ? (result as unknown as Partial<T>) : undefined;
  }

  //  If we are on an object node.
  if (dirty && typeof dirty === 'object') {
    const out: AnyObject = {};

    // Check if the key is important - then we will return the entire object
    const isImportant = (key: string): boolean => {
      if (!important) return false;
      if (Array.isArray(important)) return important.includes(key);
      if (typeof important === 'object') return Object.prototype.hasOwnProperty.call(important, key);
      return false;
    };

    Object.keys(dirty).forEach((key) => {
      if (isImportant(key)) {
        out[key] = values[key];
        return;
      }

      const child = extractDirtyValues(
        (dirty as AnyObject)[key] as FieldDirtyState<unknown>,
        values[key],

        Array.isArray(important) ? (important as I) : ((important && (important as AnyObject)[key]) as I),
      );
      if (child !== undefined) out[key] = child;
    });

    return Object.keys(out).length ? (out as Partial<T>) : undefined;
  }

  return undefined;
}

export const getEnvironment = () => {
  const host = window.location.host;

  // Check for local IPs (localhost, 127.x.x.x, 10.x.x.x, 172.16-31.x.x, 192.168.x.x)
  if (/^(localhost|127\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.|192\.168\.)/.test(host)) {
    return 'local';
  }

  const envMap: Record<string, string> = {
    local: 'local',
    draco: 'development',
    taurus: 'qa',
    staging: 'staging',
  };

  return Object.entries(envMap).find(([key]) => host.includes(key))?.[1] || 'production';
};

export const decodeToken = (token: string) => {
  return JSON.parse(atob(token.split('.')[1]));
};

export const getUsernameFromToken = (token: string): string => {
  if (!token) {
    return '';
  }
  try {
    let cleanToken = token;
    try {
      cleanToken = decodeURIComponent(token);
    } catch {
      cleanToken = token;
    }
    const parts = cleanToken.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format:', cleanToken);
      return '';
    }
    const decodedToken = decodeToken(cleanToken);
    return (decodedToken && decodedToken.sub) || '';
  } catch (error) {
    console.error('Error decoding token:', error);
    return '';
  }
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const decodedToken = decodeToken(token);
    const now = Date.now() / 1000;
    return decodedToken.exp < now;
  } catch (error) {
    return true;
  }
};
